import { create } from 'zustand';

import { appStorage } from '@/src/lib/storage';

type ThemePreference = 'system' | 'light' | 'dark';
type FeedKindPreference = 'frienzzone' | 'home';
type FeedModePreference = 'latest' | 'ranked';
type NotificationTabPreference =
  | 'all'
  | 'follows'
  | 'invites'
  | 'comments'
  | 'reactions'
  | 'mentions'
  | 'garage'
  | 'updates';

type PendingOnboardingFacebookPhoto = {
  uid: string;
  url: string;
};

type PendingInvite = {
  code: string;
};

export type PendingEmailVerificationRegistration = {
  authMethod: 'facebook' | 'google' | 'password';
  dateOfBirth: string;
  email: string;
  fullName: string;
  inviteCode: string | null;
  providerPhotoURL: string | null;
  uid: string;
};

type AppState = {
  feedModePreferences: Record<FeedKindPreference, FeedModePreference>;
  hydrated: boolean;
  notificationTabPreference: NotificationTabPreference;
  pendingEmailVerificationRegistration: PendingEmailVerificationRegistration | null;
  onboardingSeen: boolean;
  pendingInvite: PendingInvite | null;
  pendingOnboardingFacebookPhoto: PendingOnboardingFacebookPhoto | null;
  profileNavigationTargetUid: string | null;
  pwaInstallCompletedAt: string | null;
  pwaInstallSnoozedUntil: string | null;
  themePreference: ThemePreference;
  webPushDisabledAt: string | null;
  webPushToken: string | null;
  webPushPromptSnoozedUntil: string | null;
  setFeedModePreference: (
    feedKind: FeedKindPreference,
    mode: FeedModePreference,
  ) => void;
  setPendingEmailVerificationRegistration: (
    registration: PendingEmailVerificationRegistration | null,
  ) => void;
  setNotificationTabPreference: (tab: NotificationTabPreference) => void;
  setOnboardingSeen: (seen: boolean) => void;
  setPendingInvite: (invite: PendingInvite | null) => void;
  setPendingOnboardingFacebookPhoto: (
    photo: PendingOnboardingFacebookPhoto | null,
  ) => void;
  setProfileNavigationTargetUid: (uid: string | null) => void;
  setPwaInstallCompletedAt: (value: string | null) => void;
  setPwaInstallSnoozedUntil: (value: string | null) => void;
  setThemePreference: (preference: ThemePreference) => void;
  setWebPushDisabledAt: (value: string | null) => void;
  setWebPushToken: (token: string | null) => void;
  setWebPushPromptSnoozedUntil: (value: string | null) => void;
};

type PersistedAppState = Pick<
  AppState,
  | 'feedModePreferences'
  | 'notificationTabPreference'
  | 'onboardingSeen'
  | 'pendingEmailVerificationRegistration'
  | 'pendingInvite'
  | 'pendingOnboardingFacebookPhoto'
  | 'pwaInstallCompletedAt'
  | 'pwaInstallSnoozedUntil'
  | 'themePreference'
  | 'webPushDisabledAt'
  | 'webPushPromptSnoozedUntil'
  | 'webPushToken'
>;

const storageKey = 'frienzzi-app-store';
const installSnoozeMs = 7 * 24 * 60 * 60 * 1000;
const defaultState: PersistedAppState = {
  feedModePreferences: {
    frienzzone: 'ranked',
    home: 'ranked',
  },
  notificationTabPreference: 'all',
  onboardingSeen: false,
  pendingEmailVerificationRegistration: null,
  pendingInvite: null,
  pendingOnboardingFacebookPhoto: null,
  pwaInstallCompletedAt: null,
  pwaInstallSnoozedUntil: null,
  themePreference: 'system',
  webPushDisabledAt: null,
  webPushPromptSnoozedUntil: null,
  webPushToken: null,
};

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

function isFeedModePreference(value: unknown): value is FeedModePreference {
  return value === 'latest' || value === 'ranked';
}

function isNotificationTabPreference(
  value: unknown,
): value is NotificationTabPreference {
  return (
    value === 'all' ||
    value === 'follows' ||
    value === 'invites' ||
    value === 'comments' ||
    value === 'reactions' ||
    value === 'mentions' ||
    value === 'garage' ||
    value === 'updates'
  );
}

function isPendingEmailVerificationRegistration(
  value: unknown,
): value is PendingEmailVerificationRegistration {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const data = value as Record<string, unknown>;

  return (
    (data.authMethod === 'facebook' ||
      data.authMethod === 'google' ||
      data.authMethod === 'password') &&
    typeof data.dateOfBirth === 'string' &&
    typeof data.email === 'string' &&
    typeof data.fullName === 'string' &&
    (typeof data.inviteCode === 'string' || data.inviteCode === null) &&
    (typeof data.providerPhotoURL === 'string' ||
      data.providerPhotoURL === null) &&
    typeof data.uid === 'string'
  );
}

function parsePersistedState(value: string): PersistedAppState {
  const parsed = JSON.parse(value) as Partial<PersistedAppState> & {
    pwaInstallDismissedAt?: unknown;
  };

  const oldInstallDismissedAt =
    typeof parsed.pwaInstallDismissedAt === 'string'
      ? parsed.pwaInstallDismissedAt
      : null;
  const legacyInstallSnoozedUntil = (() => {
    if (!oldInstallDismissedAt) {
      return null;
    }

    const dismissedAt = new Date(oldInstallDismissedAt).getTime();

    if (!Number.isFinite(dismissedAt)) {
      return null;
    }

    const snoozedUntil = dismissedAt + installSnoozeMs;

    return snoozedUntil > Date.now()
      ? new Date(snoozedUntil).toISOString()
      : null;
  })();

  return {
    feedModePreferences: {
      frienzzone: isFeedModePreference(parsed.feedModePreferences?.frienzzone)
        ? parsed.feedModePreferences.frienzzone
        : defaultState.feedModePreferences.frienzzone,
      home: isFeedModePreference(parsed.feedModePreferences?.home)
        ? parsed.feedModePreferences.home
        : defaultState.feedModePreferences.home,
    },
    onboardingSeen:
      typeof parsed.onboardingSeen === 'boolean'
        ? parsed.onboardingSeen
        : defaultState.onboardingSeen,
    notificationTabPreference: isNotificationTabPreference(
      parsed.notificationTabPreference,
    )
      ? parsed.notificationTabPreference
      : defaultState.notificationTabPreference,
    pendingEmailVerificationRegistration:
      isPendingEmailVerificationRegistration(
        parsed.pendingEmailVerificationRegistration,
      )
        ? parsed.pendingEmailVerificationRegistration
        : null,
    pendingOnboardingFacebookPhoto:
      parsed.pendingOnboardingFacebookPhoto &&
      typeof parsed.pendingOnboardingFacebookPhoto === 'object' &&
      typeof parsed.pendingOnboardingFacebookPhoto.uid === 'string' &&
      typeof parsed.pendingOnboardingFacebookPhoto.url === 'string'
        ? parsed.pendingOnboardingFacebookPhoto
        : null,
    pendingInvite:
      parsed.pendingInvite &&
      typeof parsed.pendingInvite === 'object' &&
      typeof parsed.pendingInvite.code === 'string'
        ? { code: parsed.pendingInvite.code }
        : null,
    pwaInstallCompletedAt:
      typeof parsed.pwaInstallCompletedAt === 'string'
        ? parsed.pwaInstallCompletedAt
        : null,
    pwaInstallSnoozedUntil:
      typeof parsed.pwaInstallSnoozedUntil === 'string'
        ? parsed.pwaInstallSnoozedUntil
        : legacyInstallSnoozedUntil,
    webPushPromptSnoozedUntil:
      typeof parsed.webPushPromptSnoozedUntil === 'string'
        ? parsed.webPushPromptSnoozedUntil
        : null,
    themePreference: isThemePreference(parsed.themePreference)
      ? parsed.themePreference
      : defaultState.themePreference,
    webPushDisabledAt:
      typeof parsed.webPushDisabledAt === 'string'
        ? parsed.webPushDisabledAt
        : null,
    webPushToken:
      typeof parsed.webPushToken === 'string' ? parsed.webPushToken : null,
  };
}

export const useAppStore = create<AppState>()((set) => ({
  ...defaultState,
  hydrated: false,
  profileNavigationTargetUid: null,
  setFeedModePreference: (feedKind, mode) =>
    set((state) => ({
      feedModePreferences: {
        ...state.feedModePreferences,
        [feedKind]: mode,
      },
    })),
  setPendingEmailVerificationRegistration: (registration) =>
    set({ pendingEmailVerificationRegistration: registration }),
  setNotificationTabPreference: (tab) =>
    set({ notificationTabPreference: tab }),
  setOnboardingSeen: (seen) => set({ onboardingSeen: seen }),
  setPendingInvite: (invite) => set({ pendingInvite: invite }),
  setPendingOnboardingFacebookPhoto: (photo) =>
    set({ pendingOnboardingFacebookPhoto: photo }),
  setProfileNavigationTargetUid: (uid) =>
    set({ profileNavigationTargetUid: uid }),
  setPwaInstallCompletedAt: (value) => set({ pwaInstallCompletedAt: value }),
  setPwaInstallSnoozedUntil: (value) => set({ pwaInstallSnoozedUntil: value }),
  setThemePreference: (preference) => set({ themePreference: preference }),
  setWebPushDisabledAt: (value) => set({ webPushDisabledAt: value }),
  setWebPushToken: (token) => set({ webPushToken: token }),
  setWebPushPromptSnoozedUntil: (value) =>
    set({ webPushPromptSnoozedUntil: value }),
}));

let hydrated = false;

async function hydrateAppStore() {
  try {
    const storedValue = await appStorage.getItem(storageKey);

    if (storedValue) {
      useAppStore.setState(parsePersistedState(storedValue));
    }
  } catch (error) {
    console.error('Failed to hydrate app state.', error);
  } finally {
    hydrated = true;
    useAppStore.setState({ hydrated: true });
  }
}

void hydrateAppStore();

useAppStore.subscribe((state) => {
  if (!hydrated) {
    return;
  }

  const persistedState: PersistedAppState = {
    feedModePreferences: state.feedModePreferences,
    notificationTabPreference: state.notificationTabPreference,
    onboardingSeen: state.onboardingSeen,
    pendingEmailVerificationRegistration:
      state.pendingEmailVerificationRegistration,
    pendingInvite: state.pendingInvite,
    pendingOnboardingFacebookPhoto: state.pendingOnboardingFacebookPhoto,
    pwaInstallCompletedAt: state.pwaInstallCompletedAt,
    pwaInstallSnoozedUntil: state.pwaInstallSnoozedUntil,
    themePreference: state.themePreference,
    webPushDisabledAt: state.webPushDisabledAt,
    webPushPromptSnoozedUntil: state.webPushPromptSnoozedUntil,
    webPushToken: state.webPushToken,
  };

  void Promise.resolve(
    appStorage.setItem(storageKey, JSON.stringify(persistedState)),
  ).catch((error: unknown) =>
    console.error('Failed to persist app state.', error),
  );
});
